import { getProperty, propertyExists, setProperty } from '../../../libs/properties';
import type { TemplateColorProperties, TemplateWatermarkProperties } from '../../template/types';
import { Config } from '../../../video-data/config';
import { getAllTemplateAttributes } from '../index';
import { ErrorReporter } from '../../../libs/error-reporter';

export type TemplatePropertyKeys = TemplateColorProperties | TemplateWatermarkProperties;
type TemplateInheritKeys = typeof Config.WATERMARK_INHERIT_TEMPLATE_KEY;

const THEME_REF_PREFIX = 'themeRef_';

/**
 * All of these types must exist as a property on the Template object.
 * For example, template.colors
 */
export enum ThemeRefTypes {
    COLORS = 'colors',
    FONTS = 'fonts',
    WATERMARK_SOURCES = 'watermarkSources',
}

export type ColorsThemeRef = `themeRef_${ThemeRefTypes.COLORS}_${number}`;
export type FontsThemeRef = `themeRef_${ThemeRefTypes.FONTS}_${number}`;
export type WatermarkThemeRef = `themeRef_${ThemeRefTypes.WATERMARK_SOURCES}_${number}`;

/**
 * Builds the themeRef string which is stored in video data
 */
export function createThemeRef(type: ThemeRefTypes.COLORS, index: number): ColorsThemeRef;
// eslint-disable-next-line no-redeclare
export function createThemeRef(type: ThemeRefTypes.FONTS, index: number): FontsThemeRef;
// eslint-disable-next-line no-redeclare
export function createThemeRef(
    type: ThemeRefTypes.WATERMARK_SOURCES,
    index: number,
): WatermarkThemeRef;
// eslint-disable-next-line no-redeclare
export function createThemeRef(type: ThemeRefTypes, index: number) {
    return `${THEME_REF_PREFIX}${type}_${index}`;
}

/**
 * Returns the referenced template index for the given asset property. If the property isn't referenced, then -1 is
 * returned.
 */
export function getTemplateIndexFromAssetProperty(asset: object, propertyPath: string): number {
    const value = getTemplateProperty(asset, propertyPath);
    if (!isTemplateReference(value)) {
        return -1;
    }
    return getTemplateIndex(value);
}

const templateReferenceRegexp = /^themeRef_([a-zA-Z]{1,})_(\d{1,})$/;

export function isTemplateReference(value: unknown): value is string {
    if (typeof value !== 'string') {
        return false;
    }
    return templateReferenceRegexp.test(value);
}

export function getTemplateValueAttribute(target, attr) {
    const metadata = target.constructor[Symbol.for('Symbol.metadata')];
    if (metadata && metadata[attr]) {
        return metadata[attr].get.call(target);
    }
}

// Luminary stores filter-scoped template references in the `filters.<index>.<propName>` path
// format (e.g. `filters.0.firstColor`). BaseAsset keeps its Filter list in a private `#filters`
// field, so the generic property walker can't reach it — the public `filters` getter belongs to
// PIXI. This helper diverts filter-shaped paths through `getFilters()` so the template accessors
// below operate on the correct Luminary Filter object.
const FILTER_PATH_REGEXP = /^filters\.(\d+)\.(.+)$/;

function resolveTemplateTarget(
    object: object,
    propertyPath: string,
): { target: object; leafPath: string } | null {
    const match = propertyPath.match(FILTER_PATH_REGEXP);
    const getFilters = (object as { getFilters?: () => object[] }).getFilters;
    if (!match || typeof getFilters !== 'function') {
        return { target: object, leafPath: propertyPath };
    }
    const filter = getFilters.call(object)[Number(match[1])];
    if (!filter) return null;
    return { target: filter, leafPath: match[2] };
}

/**
 * Get the template property value for the given object's property. If no template property exists, then the property
 * value is returned.
 */
// Returns `any` for API compatibility - frontend consumers assign result without type assertions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getTemplateProperty(object: object, propertyPath: string): any {
    const resolved = resolveTemplateTarget(object, propertyPath);
    if (!resolved) return undefined;
    const { target, leafPath } = resolved;

    const propertyTokens = leafPath.split('.');

    // convert property path to template reference property path
    const nPropertyTokens = propertyTokens.length;
    const leafProperty = `${propertyTokens[nPropertyTokens - 1]}`;

    const templatePropertyTokens = propertyTokens.slice(0, nPropertyTokens - 1);
    const obj = getProperty(target, templatePropertyTokens);
    if (obj === undefined) {
        return undefined;
    }
    const templatePropertyValue = getTemplateValueAttribute(obj, leafProperty);
    if (templatePropertyValue === undefined) {
        // Either the property is not a template property or the template setter has never been
        // called (so `_{property}Value` has not been defined yet). In this case we can just
        // return the property directly
        return obj[leafProperty];
    }
    return templatePropertyValue;
}

/**
 * Set a property value on an object by path, routing `filters.<index>.<propName>` paths to the
 * correct Luminary Filter via `getFilters()`. Intended for assigning template reference values
 * (e.g. `templateColorByIndex(n)`) through attribute setters.
 */
export function setTemplateProperty(object: object, propertyPath: string, value: unknown): void {
    const resolved = resolveTemplateTarget(object, propertyPath);
    if (!resolved) return;
    const { target, leafPath } = resolved;
    const pathComponents = leafPath.split('.');
    if (!propertyExists(target, pathComponents)) return;
    setProperty(target, pathComponents, value);
}

/** Returns true if the given object has any properties which have a template value */
export function objectHasTemplateValues(object: object) {
    return getAllTemplateAttributes(object).some((attr) => propertyHasTemplateValue(object, attr));
}

/** Returns true if the given property is set to a template value */
export function propertyHasTemplateValue(object: object, propertyPath: string): boolean {
    return isTemplateReference(getTemplateProperty(object, propertyPath));
}

/**
 * Get the index of the given template reference. A valid template reference such as themeRef_colors_1 has 3 pieces:
 * the prefix "themeRef", the type "color" and index "1". This method returns the index.
 */
export function getTemplateIndex(templateReference: string): number {
    const colorTokens = templateReference.split('_');
    if (colorTokens.length !== 3) {
        throw Error(`Invalid number of tokens. Expected 3 but got ${colorTokens.length}`);
    }
    return parseInt(colorTokens[2]);
}

export function validateTemplateReference(value: unknown, templateProperty: ThemeRefTypes): void {
    if (!isTemplateReference(value)) {
        throw Error(`Invalid template reference: ${value}`);
    }
    const templateIndex = getTemplateIndex(value);

    if (typeof this.template === 'undefined') {
        ErrorReporter.setContext('template', {
            name: this.name,
            value,
            templateIndex,
            templateProperty,
            template: this['template'],
        });
        throw Error('Unable to access to template');
    }

    const length = this['template'][templateProperty].length;
    if (templateIndex >= length) {
        ErrorReporter.setContext('template', {
            name: this.name,
            value,
            templateIndex,
            length,
            templateProperty,
            template: this['template'],
        });
        throw Error('Out of bounds trying to access template property');
    }
}

export function createTemplateAttributeGetter(
    getOriginal,
    themeRefType: ThemeRefTypes,
    valueKey?: TemplatePropertyKeys,
    inheritKey?: TemplateInheritKeys,
) {
    return function get() {
        const value = getOriginal.call(this);
        return resolveTemplateValue(this, value, themeRefType, valueKey, inheritKey);
    };
}

/**
 * This is where we look up the theme reference on the Template object directly.
 */
function resolveTemplateValue(
    object: { template?: object; name?: string },
    value: unknown,
    templateProperty: ThemeRefTypes,
    valueKey?: TemplatePropertyKeys,
    inheritKey?: TemplateInheritKeys,
) {
    if (isTemplateReference(value)) {
        const templateIndex = getTemplateIndex(value);
        if (!object['template']) {
            throw Error(`Unable to access template from ${object['name']}`);
        }
        if (templateIndex >= object['template'][templateProperty].length) {
            throw Error(
                `Out of bounds. Trying to access ${templateProperty} at index ${templateIndex} of ${object['template'][templateProperty].length - 1}`,
            );
        }
        let propertyPath = object['template'][templateProperty][templateIndex];
        if (valueKey) {
            propertyPath = propertyPath[valueKey];
        }
        return propertyPath;
    } else if (value === Config.INHERIT_FROM_TEMPLATE) {
        if (!object['template']) {
            throw Error(`Unable to access template from ${object['name']}`);
        }
        if (!inheritKey) {
            throw Error(
                `Trying to access inherited value on template without defining the key ${object['name']}`,
            );
        }
        if (!valueKey) {
            throw Error(`A valueKey is needed to support inherit ${object['name']}`);
        }
        return object['template'][inheritKey][valueKey];
    } else {
        return value;
    }
}
